
import { Component, Prop, Watch, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { ValidationObserver } from 'vee-validate';
import Toast from '@/utils/toast';
import InfoBanner from '@/components/shared/suites/InfoBanner.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import TagLocaleEditor from '@/components/menu/TagLocaleEditor.vue';
import TagFormFields from '@/components/menu/TagFormFields.vue';

const namespace = 'tags';

@Component({
	components: {
		ValidationObserver,
		InfoBanner,
		TextButton,
		TagLocaleEditor,
		TagFormFields
	}
})
export default class TagEditorModal extends Vue {
	@Action('createTag', { namespace }) private createTag!: (tagData: Partial<MenuItemTag>) => Promise<void>;
	@Action('updateTag', { namespace }) private updateTag!: (payload: { tagName: string, tagData: Partial<MenuItemTag> }) => Promise<void>;
	@Action('fetchTags', { namespace }) private fetchTags!: () => Promise<void>;
	@Getter('getAllTags', { namespace }) private tags!: MenuItemTag[];
	@Prop({ type: Object, default: null }) private editingTag!: MenuItemTag | null;
	@Prop({ type: Object, default: () => {} }) private selectedLocale!: RestaurantLocale;
	@Watch('editingTag', { immediate: true })
	private onEditingTagChange(tag: MenuItemTag | null): void {
		if (tag) {
			this.tagForm = {
				name: tag.name,
				icon: tag.icon || 'mdi-heart',
				colorHex: tag.color || '#757575',
				localization: tag.localization ? JSON.parse(JSON.stringify(tag.localization)) : undefined
			};
		}
		else {
			this.resetForm();
		}
	}

	$refs!: { observer: InstanceType<typeof ValidationObserver> };

	private loading: boolean = false;

	private tagForm: TagForm = {
		name: '',
		icon: 'mdi-heart',
		colorHex: '#757575'
	};

	/**
	 * Validates the form and creates or updates a tag.
	 * Handles stale tag detection during edits to prevent conflicts.
	 *
	 * @return {Promise<void>}
	 */
	private async saveTag(): Promise<void> {
		// Validate form before proceeding
		const isValid = await this.$refs.observer.validate();
		if (!isValid) {
			return;
		}

		this.loading = true;

		const tagData: MenuItemTagForm = {
			name: this.tagForm.name,
			icon: this.tagForm.icon,
			color: this.tagForm.colorHex
		};

		if (this.tagForm.localization) {
			tagData.localization = this.tagForm.localization;
		}

		try {
			if (this.editingTag) {
				// Check if the tag still exists to prevent updating a deleted tag
				const tagStillExists = this.tags.some(t => t.name === this.editingTag!.name);

				if (!tagStillExists) {
					// Tag was deleted externally; refresh the list and close
					await this.fetchTags();
					this.cancelDialog();
					return;
				}

				// Update existing tag
				await this.updateTag({
					tagName: this.editingTag.name,
					tagData
				});
			}
			else {
				// Create new tag
				await this.createTag(tagData);
			}

			this.cancelDialog();
		}
		catch (errorMessage) {
			const errorKey = this.editingTag
				? 'menu.tags.errors.error_updating'
				: 'menu.tags.errors.error_creating';
			Toast.error(this.$t(errorKey, { errorMessage }));
		}
		finally {
			this.loading = false;
		}
	}

	private cancelDialog(): void {
		this.resetForm();
		this.$emit('close');
	}

	private resetForm(): void {
		this.tagForm = {
			name: '',
			icon: 'mdi-heart',
			colorHex: '#757575'
		};
		this.$refs.observer?.reset();
	}
}
